import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Modal,
  IconButton,
  Chip,
  CircularProgress,
  alpha,
  useTheme,
  Tooltip,
  Slider,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import RepeatIcon from '@mui/icons-material/Repeat';
import PlaceIcon from '@mui/icons-material/Place';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { envisFindingsApi } from '../../services/api';
import AuthenticatedImage from '../common/AuthenticatedImage';

const DefectProgressionModal = ({ open, onClose, defectId, defectInfo }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showBbox, setShowBbox] = useState(true);
  const [zoom, setZoom] = useState({ scale: 1, translateX: 0, translateY: 0 });
  const playIntervalRef = useRef(null);
  const imageAreaRef = useRef(null);
  const imageRef = useRef(null);
  const imageWrapperRef = useRef(null);

  const displayIndex = activeIndex;
  const isZoomed = zoom.scale > 1;

  // Fetch progression data
  useEffect(() => {
    if (!open || !defectId) return;

    const fetch = async () => {
      setLoading(true);
      setError(null);
      setActiveIndex(0);
      setIsPlaying(false);
      try {
        const response = await envisFindingsApi.getDefectProgression(defectId);
        setData(response.data);
      } catch (err) {
        console.error('Error fetching defect progression:', err);
        setError('Failed to load defect progression');
      } finally {
        setLoading(false);
      }
    };

    fetch();
  }, [open, defectId]);

  // Auto-play
  useEffect(() => {
    if (isPlaying && data?.timeline?.length > 1) {
      playIntervalRef.current = setInterval(() => {
        setActiveIndex(prev => {
          const next = prev + 1;
          if (next >= data.timeline.length) {
            setIsPlaying(false);
            return 0;
          }
          return next;
        });
      }, 1500);
    }
    return () => {
      if (playIntervalRef.current) clearInterval(playIntervalRef.current);
    };
  }, [isPlaying, data?.timeline?.length]);

  // Stop play on close
  useEffect(() => {
    if (!open) {
      setIsPlaying(false);
      if (playIntervalRef.current) clearInterval(playIntervalRef.current);
    }
  }, [open]);

  // Reset zoom when switching detections
  useEffect(() => {
    setZoom({ scale: 1, translateX: 0, translateY: 0 });
  }, [activeIndex]);

  const zoomToDefect = () => {
    const img = imageRef.current;
    const container = imageAreaRef.current;
    const currentItem = (data?.timeline || [])[activeIndex];
    if (!img || !container || !currentItem || currentItem.x1 == null) return;

    const containerRect = container.getBoundingClientRect();
    const imgRect = img.getBoundingClientRect();

    // Bbox center in the rendered image's pixel space
    const bboxCenterXPct = (currentItem.x1 + currentItem.x2) / 2 / currentItem.imageWidth;
    const bboxCenterYPct = (currentItem.y1 + currentItem.y2) / 2 / currentItem.imageHeight;
    const bboxWidthPct = (currentItem.x2 - currentItem.x1) / currentItem.imageWidth;
    const bboxHeightPct = (currentItem.y2 - currentItem.y1) / currentItem.imageHeight;

    // Rendered bbox size in pixels (relative to the wrapper, not the container)
    const wrapperRect = imageWrapperRef.current?.getBoundingClientRect();
    if (!wrapperRect) return;

    const bboxW = bboxWidthPct * wrapperRect.width;
    const bboxH = bboxHeightPct * wrapperRect.height;

    // Scale to fit bbox with 40% padding on each side
    const paddingFactor = 1.8;
    const scaleX = containerRect.width / (bboxW * paddingFactor);
    const scaleY = containerRect.height / (bboxH * paddingFactor);
    const targetScale = Math.min(scaleX, scaleY, 6);

    // Bbox center in wrapper pixel space
    const bboxCX = bboxCenterXPct * wrapperRect.width;
    const bboxCY = bboxCenterYPct * wrapperRect.height;

    // Wrapper offset from container center (before zoom)
    const wrapperOffsetX = wrapperRect.left - containerRect.left;
    const wrapperOffsetY = wrapperRect.top - containerRect.top;

    // Translate so that bbox center lands at container center
    // visual_x = wrapperOffsetX + S*px + TX  =>  TX = containerW/2 - wrapperOffsetX - S*bboxCX
    const translateX = containerRect.width / 2 - wrapperOffsetX - targetScale * bboxCX;
    const translateY = containerRect.height / 2 - wrapperOffsetY - targetScale * bboxCY;

    setZoom({ scale: targetScale, translateX, translateY });
  };

  const resetZoom = () => {
    setZoom({ scale: 1, translateX: 0, translateY: 0 });
  };

  const normalizeImagePath = (path) => {
    if (!path || path === 'no-image') return null;
    return path.replace(/\\/g, '/');
  };

  if (!open) return null;

  const timeline = data?.timeline || [];
  const defectMeta = data?.defect || defectInfo || {};
  const currentItem = timeline[displayIndex];

  const location = [defectMeta.networkName, defectMeta.sectionName, defectMeta.chainageName]
    .filter(Boolean).join(' \u2022 ');

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  };

  // Calculate days between first and current detection
  const daysSinceFirst = currentItem && timeline[0]
    ? Math.round((new Date(currentItem.detectedAt) - new Date(timeline[0].detectedAt)) / (1000 * 60 * 60 * 24))
    : 0;

  const accentColor = theme.palette.error.main;

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{
        position: 'absolute',
        top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '95%', sm: '85%', md: '75%', lg: '65%' },
        maxWidth: 1000,
        maxHeight: '92vh',
        bgcolor: 'background.paper',
        borderRadius: 3,
        boxShadow: 24,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <Box sx={{
          px: 2.5, py: 1.5,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          flexShrink: 0,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <RepeatIcon sx={{ color: accentColor }} />
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                  Defect Progression
                </Typography>
                <Chip label={`D-${defectMeta.id || defectId}`} size="small" variant="outlined"
                  sx={{ fontFamily: 'monospace', fontSize: '0.7rem', height: 22 }} />
                {defectMeta.defectType && (
                  <Chip label={defectMeta.defectType} size="small"
                    sx={{ fontSize: '0.65rem', height: 20, bgcolor: alpha(accentColor, 0.1), color: accentColor }} />
                )}
              </Box>
              {location && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                  <PlaceIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    {location}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
        </Box>

        {/* Body */}
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10 }}>
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Loading progression...</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <Typography variant="body2" color="error">{error}</Typography>
          </Box>
        ) : (
          <>
            {/* Main Image Area */}
            <Box
              ref={imageAreaRef}
              sx={{
                position: 'relative',
                width: '100%',
                height: { xs: 300, sm: 380, md: 440 },
                bgcolor: '#1a1a1a',
                overflow: 'hidden',
                userSelect: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {currentItem?.imagePath ? (
                <Box
                  ref={imageWrapperRef}
                  sx={{
                    position: 'relative',
                    maxWidth: '100%',
                    maxHeight: '100%',
                    lineHeight: 0,
                    transform: `scale(${zoom.scale}) translate(${zoom.translateX / zoom.scale}px, ${zoom.translateY / zoom.scale}px)`,
                    transformOrigin: '0 0',
                    transition: 'transform 0.35s ease-out',
                  }}
                >
                  <AuthenticatedImage
                    ref={imageRef}
                    src={normalizeImagePath(currentItem.imagePath)}
                    alt={`Detection ${displayIndex + 1}`}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: { xs: 300, sm: 380, md: 440 },
                      objectFit: 'contain',
                      display: 'block',
                    }}
                  />

                  {/* Bounding Box Overlay — positioned relative to the image element */}
                  {showBbox && currentItem.x1 != null && (
                    <Box sx={{
                      position: 'absolute',
                      border: `${Math.max(1, 2 / zoom.scale)}px solid ${currentItem.type === 'original' ? accentColor : theme.palette.warning.main}`,
                      borderRadius: '3px',
                      backgroundColor: alpha(currentItem.type === 'original' ? accentColor : theme.palette.warning.main, 0.12),
                      pointerEvents: 'none',
                      left: `${(currentItem.x1 / currentItem.imageWidth) * 100}%`,
                      top: `${(currentItem.y1 / currentItem.imageHeight) * 100}%`,
                      width: `${((currentItem.x2 - currentItem.x1) / currentItem.imageWidth) * 100}%`,
                      height: `${((currentItem.y2 - currentItem.y1) / currentItem.imageHeight) * 100}%`,
                    }} />
                  )}
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography variant="body2" color="grey.500">No image available</Typography>
                </Box>
              )}

              {/* Top-left: Detection type badge */}
              <Chip
                size="small"
                label={currentItem?.type === 'original' ? 'First Detection' : `Re-detection #${displayIndex}`}
                sx={{
                  position: 'absolute', top: 12, left: 12,
                  bgcolor: currentItem?.type === 'original' ? accentColor : theme.palette.warning.main,
                  color: 'white', fontWeight: 600, fontSize: '0.7rem', height: 24,
                }}
              />

              {/* Top-right: Date and day count */}
              <Box sx={{
                position: 'absolute', top: 12, right: 12,
                bgcolor: 'rgba(0,0,0,0.75)', color: 'white',
                px: 1.5, py: 0.75, borderRadius: 2,
                display: 'flex', flexDirection: 'column', alignItems: 'flex-end',
              }}>
                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                  {formatDateTime(currentItem?.detectedAt)}
                </Typography>
                {daysSinceFirst > 0 && (
                  <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.7)' }}>
                    Day +{daysSinceFirst} since first detection
                  </Typography>
                )}
              </Box>

              {/* Bottom-left: Confidence and distance */}
              {currentItem?.type === 'repeat' && (
                <Box sx={{
                  position: 'absolute', bottom: 12, left: 12,
                  bgcolor: 'rgba(0,0,0,0.75)', color: 'white',
                  px: 1.5, py: 0.5, borderRadius: 2,
                  fontSize: '0.75rem',
                }}>
                  {currentItem.confidenceScore != null && (
                    <span>Confidence: {(currentItem.confidenceScore * 100).toFixed(0)}%</span>
                  )}
                  {currentItem.distanceMeters != null && (
                    <span> &bull; {currentItem.distanceMeters.toFixed(1)}m from original</span>
                  )}
                </Box>
              )}

              {/* Bottom-right: counter */}
              <Box sx={{
                position: 'absolute', bottom: 12, right: 12,
                bgcolor: 'rgba(0,0,0,0.75)', color: 'white',
                px: 1.5, py: 0.5, borderRadius: 2,
                fontSize: '0.8rem', fontWeight: 600, fontFamily: 'monospace',
              }}>
                {displayIndex + 1} / {timeline.length}
              </Box>

              {/* Image controls: bbox toggle + zoom */}
              <Box sx={{
                position: 'absolute', top: 12, right: 200,
                display: 'flex', gap: 0.5, zIndex: 10,
              }}>
                <Tooltip title={showBbox ? 'Hide bounding box' : 'Show bounding box'} placement="bottom">
                  <IconButton
                    size="small"
                    onClick={() => setShowBbox(prev => !prev)}
                    sx={{
                      bgcolor: showBbox ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.6)',
                      color: showBbox ? accentColor : 'rgba(255,255,255,0.8)',
                      '&:hover': { bgcolor: showBbox ? 'white' : 'rgba(0,0,0,0.8)' },
                      width: 30, height: 30,
                    }}
                  >
                    {showBbox ? <VisibilityIcon sx={{ fontSize: 16 }} /> : <VisibilityOffIcon sx={{ fontSize: 16 }} />}
                  </IconButton>
                </Tooltip>
                <Tooltip title={isZoomed ? 'Reset view' : 'Zoom to defect'} placement="bottom">
                  <IconButton
                    size="small"
                    onClick={isZoomed ? resetZoom : zoomToDefect}
                    disabled={!currentItem || currentItem.x1 == null}
                    sx={{
                      bgcolor: isZoomed ? alpha(theme.palette.primary.main, 0.9) : 'rgba(255,255,255,0.9)',
                      color: isZoomed ? 'white' : theme.palette.primary.main,
                      '&:hover': { bgcolor: isZoomed ? theme.palette.primary.main : 'white' },
                      '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.4)', color: 'rgba(0,0,0,0.3)' },
                      width: 30, height: 30,
                    }}
                  >
                    {isZoomed ? <ZoomOutMapIcon sx={{ fontSize: 16 }} /> : <ZoomInIcon sx={{ fontSize: 16 }} />}
                  </IconButton>
                </Tooltip>
              </Box>

            </Box>

            {/* Timeline Controls */}
            <Box sx={{
              px: 2.5, py: 1.5,
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
              display: 'flex', alignItems: 'center', gap: 1.5,
              flexShrink: 0,
            }}>
              {/* Playback controls */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton size="small" onClick={() => { setActiveIndex(0); setIsPlaying(false); }}
                  disabled={timeline.length <= 1}>
                  <SkipPreviousIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => {
                    if (isPlaying) {
                      setIsPlaying(false);
                    } else {
                      if (activeIndex >= timeline.length - 1) setActiveIndex(0);
                      setIsPlaying(true);
                    }
                  }}
                  disabled={timeline.length <= 1}
                  sx={{
                    bgcolor: isPlaying ? alpha(accentColor, 0.1) : 'transparent',
                    color: isPlaying ? accentColor : 'inherit',
                  }}
                >
                  {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                </IconButton>
                <IconButton size="small"
                  onClick={() => { setActiveIndex(timeline.length - 1); setIsPlaying(false); }}
                  disabled={timeline.length <= 1}>
                  <SkipNextIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* Timeline slider */}
              <Box sx={{ flex: 1, px: 1 }}>
                <Slider
                  value={displayIndex}
                  min={0}
                  max={Math.max(timeline.length - 1, 0)}
                  step={1}
                  onChange={(_, val) => { setActiveIndex(val); setIsPlaying(false); }}
                  marks={timeline.map((item, i) => ({
                    value: i,
                    label: '',
                  }))}
                  sx={{
                    color: accentColor,
                    height: 4,
                    '& .MuiSlider-thumb': {
                      width: 14, height: 14,
                      '&:hover, &.Mui-focusVisible': {
                        boxShadow: `0 0 0 6px ${alpha(accentColor, 0.2)}`,
                      },
                    },
                    '& .MuiSlider-mark': {
                      width: 6, height: 6, borderRadius: '50%',
                      bgcolor: alpha(accentColor, 0.4),
                      transform: 'translate(-50%, -50%)',
                    },
                    '& .MuiSlider-markActive': {
                      bgcolor: accentColor,
                    },
                  }}
                />
              </Box>

              {/* Date range labels */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                  {formatDate(timeline[0]?.detectedAt)} - {formatDate(timeline[timeline.length - 1]?.detectedAt)}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>
                  {data?.totalDetections} detections across {data?.distinctPatrolDays} patrols
                </Typography>
              </Box>
            </Box>

            {/* Thumbnail Strip */}
            <Box sx={{
              px: 2, pb: 2, pt: 0.5,
              display: 'flex', gap: 1,
              overflowX: 'auto',
              flexShrink: 0,
              '&::-webkit-scrollbar': { height: 4 },
              '&::-webkit-scrollbar-thumb': { bgcolor: alpha(theme.palette.divider, 0.3), borderRadius: 2 },
            }}>
              {timeline.map((item, i) => (
                <Tooltip key={item.id} title={formatDateTime(item.detectedAt)} placement="top" arrow>
                  <Box
                    onClick={() => { setActiveIndex(i); setIsPlaying(false); }}
                    sx={{
                      flexShrink: 0,
                      width: 64, height: 40,
                      borderRadius: 1,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: '2px solid',
                      borderColor: i === activeIndex ? accentColor : 'transparent',
                      opacity: i === activeIndex ? 1 : 0.6,
                      transition: 'all 0.15s ease',
                      position: 'relative',
                      '&:hover': { opacity: 1, borderColor: alpha(accentColor, 0.5) },
                    }}
                  >
                    {item.imagePath ? (
                      <AuthenticatedImage
                        src={normalizeImagePath(item.imagePath)}
                        alt={`Detection ${i + 1}`}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <Box sx={{ width: '100%', height: '100%', bgcolor: 'grey.200' }} />
                    )}
                    {/* Tiny index badge */}
                    <Box sx={{
                      position: 'absolute', bottom: 1, right: 1,
                      bgcolor: item.type === 'original' ? accentColor : 'rgba(0,0,0,0.6)',
                      color: 'white', fontSize: '0.5rem', fontWeight: 700,
                      px: 0.4, borderRadius: 0.5, lineHeight: 1.3,
                    }}>
                      {item.type === 'original' ? '1st' : i}
                    </Box>
                  </Box>
                </Tooltip>
              ))}
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default DefectProgressionModal;
